<template>
  <div class="chat-room">
    <div
      v-if="showSidebar"
      class="messages-sidebar"
      :class="{show: mobileShowSidebar}"
    >
      <div class="sidebar-close-button">
        <a
          href="#"
          @click.prevent="mobileShowSidebar = false"
        >
          &times;
        </a>
      </div>

      <chat-status
        v-if="serverUser"
        :user="serverUser"
        :value="chatStatus"
        @input="changeChatStatus($event)"
      />
      <hr>
      <chat-room-members
        :user="user"
        :members="chatMembers"
      />
    </div>
    <div class="messages-box">
      <div
        ref="messages"
        class="messages"
      >
        <div
          v-if="status === 'loading'"
          class="loading"
        >
          Loading previous messages...
        </div>
        <div
          v-else-if="status === 'full'"
          class="loading"
        >
          No other messages here
        </div>
        <transition-group tag="div" name="pop">
          <div
            v-if="messages.length === 0"
            key="empty"
            class="empty"
          >
            <slot name="empty">
              <i>{{ emptyMessage }}</i>
            </slot>
          </div>
          <div
            v-for="message of messages"
            :key="message.id"
            class="message"
            :class="{other: message.sender_id !== user.id}"
            :style="{'transition-delay': `${message.delay || 0}ms`}"
          >
            <slot
              name="message-user"
              :message="message"
              :afterImageLoads="afterImageLoads"
              :user="user"
            >
              <div class="user">
                {{ message.sender }}
              </div>
            </slot>
            <slot
              name="message"
              :message="message"
              :user="user"
              :afterImageLoads="afterImageLoads"
            >
              <div class="chat-bubble">
                <slot
                  name="message-image"
                  v-if="message.msg_type === 'image'"
                  :message="message"
                >
                  <img
                    :src="message.upload"
                    @load="afterImageLoads"
                    @click="$emit('click', message)"
                  >
                </slot>
                <div v-if="message.msg_type === 'file'" class="download">
                  <div v-if="message.sender_id != user.id && !readDownloadWarning" class="warning">
                    <p>
                      <b>You are being offered a file</b><br>
                      <br>
                      Downloading files from the internet can allow
                      malicious agents to control your computer and steal from you.<br>
                      <br>
                      Only download files from people you know and trust.<br>
                      Never run a program downloaded from chat.<br>
                    </p>
                    <div class="right">
                      <button @click="setReadDownloadWarning" type="button">
                        I'll be careful
                      </button>
                    </div>
                  </div>
                  <div v-else>
                    <i v-if="message.sender_id != user.id">Uploaded</i>
                    <i v-else>You uploaded</i>:
                    <a
                      :download="message.upload|basename"
                      :href="message.upload"
                    >
                      {{ message.upload|basename }}
                    </a>
                  </div>
                </div>
                <p v-else>{{ message.message }}</p>
                <div
                  v-if="message.timestamp"
                  class="timestamp"
                >
                  {{ new Date(message.timestamp).toLocaleString() }}
                </div>
                <div
                  v-else
                  class="send-status"
                >
                  <template v-if="message.send_status === 'sending'">
                    Sending...
                  </template>
                  <template v-else-if="message.send_status === 'failed'">
                    Failed to send.
                    <a
                      href="javascript:void(0);"
                      @click="handleSend(message)"
                    >
                      Try again
                    </a>
                  </template>
                </div>
              </div>
              <div class="spacer" />
            </slot>
          </div>
        </transition-group>
      </div>
      <form
        class="message-form"
        @submit.prevent="handleSend()"
      >
        <div
          v-if="uploadError"
          class="alert alert-error"
        >
          {{ uploadError }}
        </div>
        <div class="input-row">
          <slot name="input">
            <button
              @click="$refs.file.click()"
              type="button"
              class="attachment-button btn btn-outline-light"
            >
              📎
            </button>
            <input
              type="file"
              ref="file"
              @input="handleUpload"
            />
            <textarea
              ref="typer"
              v-model="message"
              class="form-control"
              @keypress="handleEnterSubmit"
            />
            <button class="btn btn-primary">
              Send
            </button>
          </slot>
        </div>
        <div class="sidebar-button">
          <a
            href="#"
            class="btn btn-link btn-sm"
            @click.prevent="mobileShowSidebar = !mobileShowSidebar"
          >
            Show Members
          </a>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { v1 as uuidv1 } from 'uuid';
import ChatStatus from './ChatStatus.vue';
import ChatRoomMembers from './ChatRoomMembers.vue';

import ChatMixin from '../mixins/ChatMixin';


export default {
  name: 'ChatRoom',
  components: {
    ChatStatus,
    ChatRoomMembers,
  },
  mixins: [ChatMixin],
  props: {
    room: { type: String, default: '__default__' },
    showSidebar: { type: Boolean, default: true },
    emptyMessage: { type: String, default: null },
  },
  data() {
    return {
      readDownloadWarning: this.$root.get('chat:readDownloadWarning', false),
      message: '',
      uploadError: null,
      page: 1,
      status: null,
      scrollHeight: 0,
      mobileShowSidebar: false,
    };
  },

  watch: {
    async room(newValue) {
      await this.wsLoggedInPromise;
      await this.joinChatRoom(newValue);
    },
  },
  async mounted() {
    await this.wsLoggedInPromise;

    // join room
    // TODO: only if logged in
    await this.joinChatRoom(this.room);

    // load previous messages
    await this.loadPrevious();
    this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;

    // TODO: encryption
    this.scrollListener = this.handleScroll.bind(this);
    this.$refs.messages.addEventListener('scroll', this.scrollListener, false);
    this.listener('chat:before:chat_message', this.recordScroll);
    this.listener('chat:after:chat_message', this.afterChatMessage);
    this.listener('ws:login', this.loadPrevious);
  },
  methods: {
    setReadDownloadWarning() {
      this.readDownloadWarning = true;
      this.$root.put('chat:readDownloadWarning', true);
    },
    recordScroll() {
      if (this.$refs.messages) {
        this.scrollHeight = this.$refs.messages.scrollHeight;
      }
    },
    async afterImageLoads(img) {

      // scroll to view message
      // const el = this.$refs.messages;
      // if (el) {
      //   if (this.scrollHeight - el.scrollTop < window.screen.height * 1.5) {
      //     el.scrollTop += el.scrollHeight - this.scrollHeight;
      //   }
      // }
    },
    async afterChatMessage(message) {

      // scroll to view message
      const el = this.$refs.messages;
      if (el) {
        if (this.scrollHeight - el.scrollTop < window.screen.height * 1.5) {
          el.scrollTop += el.scrollHeight - this.scrollHeight;
        }
      }

      if (this.page > 0) {
        this.resetPages();
      }

      // TODO: if user has seen this message
      // TODO: if active (looking at this tab)
      // TODO: if awake (has touched the keyboard)
      // TODO: add handler to wait for these conditions if they aren't met
    },
    resetPages() {
      const el = this.$refs.messages;
      if (el) {
        if (el.scrollHeight - window.screen.height <= el.scrollTop) {
          clearTimeout(this.resetPagesTimeout);
          this.resetPagesTimeout = setTimeout(() => {
            this.page = 1;
            const len = this.messages.length;
            this.messages = this.messages.slice(len - 15, len);
          }, 1000 * 0);
        }
      }
    },
    // load previous messages on scroll
    handleScroll() {
      const h = 0; // window.screen.height / 4;
      if (this.status === null && this.$refs.messages.scrollTop <= h) {
        this.page += 1;
        this.loadPrevious(this.page);
      }
    },
    async handleUpload(e) {
      for (const file of e.target.files) {
        let url = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target.result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        e.target.value = null;

        const msgType = /^data:image/.test(url) ? 'image' : 'file';

        const response = await this.api({
          url: 'chat/',
          method: 'POST',
          json: {
            message: '',
            room: this.room,
            filename: file.name,
            content: url,
            msg_type: msgType,
          }
        });

        if (!response.ok) {
          this.uploadError = response.error;
        }
      }
    },
    async loadPrevious(page = null) {

      if (typeof page !== 'number') {
        page = null;
      }

      this.status = 'loading';
      let pageNumber = page;
      if (page === null) {
        // this.messages = [];
        pageNumber = 1;
        this.page = 1;
      }

      const response = await this.api({
        url: 'chat/',
        params: {
          chat_room: this.room,
          page: pageNumber,
        },
      });
      if (response.status === 200) {
        const temp = this.$refs.messages.scrollHeight;

        if (response.body.results) {
          let i = 15;
          for (const m of response.body.results) {
            if (!this.messages.find(m2 => m2.id === m.id)) {
              m.delay = i * 30;
              i -= 1;
              this.messages.unshift(m);
            }
          }
        } else {
          this.messages = [];
        }

        await new Promise(r => setTimeout(r, 100));
        this.$refs.messages.scrollTop += (
          this.$refs.messages.scrollHeight - temp
        );

        await new Promise(r => setTimeout(r, 10));
        this.status = null;
      } else {
        this.status = 'full';
      }
    },
    async handleEnterSubmit(e) {
      if (e.keyCode === 13 && !e.shiftKey) {
        this.handleSend();
      }
    },
    async handleSend(message) {
      if (message || this.message.trim() !== '') {
        let encNonce;
        let response;

        if (message) {
          // This is a message that failed to send

          encNonce = message.enc_nonce;
          response = await this.sendChatMessage(message);
        } else {
          // This is a new message

          encNonce = uuidv1();
          response = await this.sendChatMessage(this.message, encNonce);
          this.message = '';
        }

        if (!response.ok) {
          const theMessage = this.messages.find((m) => m.id === encNonce);

          if (theMessage) {
            theMessage.send_status = 'failed';
          }
        }
      }
    },
  },
  filters: {
    basename(url) {
      const parts = url.split('/');
      return parts[parts.length - 1];
    }
  },
};
</script>

<style scoped>
input[type=file] {
  display: none;
}
.sidebar-button {
  display: none;
}
.chat-room {
  max-height: 100%;
  display: flex;
  flex-direction: row;
}
.messages-sidebar {
  position: static;
}
.messages-box {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}
.messages {
  overflow-y: auto;
  overflow-x: hidden;
}
.sidebar-close-button {
  display: none;
}

@media (max-width: 1024px) {
  .chat-room {
    grid-template-columns: 0px 1fr;
  }
  .sidebar-button {
    display: block;
  }
  .sidebar-close-button {
    display: block;
  }
  .messages-sidebar {
    display: none;
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    background: white;
  }
  .messages-sidebar.show {
    display: block;
  }
  .messages-column {
    align-items: stretch;
    justify-content: stretch;
    height: 100%;
  }
}
</style>
